import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import { BOARD_SIZE, PieceVisual } from "../../pages/puzzles/jigsaw/PieceVisual";
import jigsawSvg8 from "./jigsaw-8.svg";
import jigsawSvg10 from "./jigsaw-10.svg";
import jigsawSvg12 from "./jigsaw-12.svg";
import piecePaths8 from "./jigsawPiecePaths-8.json";
import piecePaths10 from "./jigsawPiecePaths-10.json";
import piecePaths12 from "./jigsawPiecePaths-12.json";
import JigsawDieLineOverlay from "../../pages/puzzles/jigsaw/JigsawDieLineOverlay";

const STORY_ID = "c238cdb1-70fc-4a66-bc92-6b7c9edb6ce8";

function newBoardPieceId() {
    return `bp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
}

function JigsawSection({ imageUrl, grid, paths, svg }) {
    const cellPx = BOARD_SIZE / grid;
    const total = grid * grid;
    const [boardPieces, setBoardPieces] = useState([]);
    const [dragging, setDragging] = useState(null);
    const boardRef = useRef(null);
    const trayRef = useRef(null);

    const handleMouseDown = useCallback((e, pieceIndex, { fromTray, boardId, boardX, boardY }) => {
        e.preventDefault();
        const cp = BOARD_SIZE / grid;
        let grabDx = cp / 2;
        let grabDy = cp / 2;
        if (!fromTray && boardRef.current && boardX != null && boardY != null) {
            const rect = boardRef.current.getBoundingClientRect();
            grabDx = e.clientX - rect.left - boardX;
            grabDy = e.clientY - rect.top - boardY;
        }
        setDragging({
            pieceIndex,
            x: e.clientX,
            y: e.clientY,
            fromTray: !!fromTray,
            boardId: boardId ?? null,
            grabDx,
            grabDy,
        });
    }, [grid]);

    useEffect(() => {
        if (!dragging) return;
        const cellPxN = BOARD_SIZE / grid;
        const tabMargin = cellPxN * 0.35;

        const onMove = (e) => setDragging((p) => (p ? { ...p, x: e.clientX, y: e.clientY } : null));
        const onUp = (e) => {
            const { pieceIndex, fromTray, boardId, grabDx, grabDy } = dragging;

            const onBoard =
                boardRef.current &&
                (() => {
                    const rect = boardRef.current.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    return x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE;
                })();

            if (onBoard && boardRef.current) {
                const rect = boardRef.current.getBoundingClientRect();
                let nx = e.clientX - rect.left - grabDx;
                let ny = e.clientY - rect.top - grabDy;
                nx = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, nx));
                ny = Math.max(-tabMargin, Math.min(BOARD_SIZE - cellPxN + tabMargin, ny));

                const homeX = (pieceIndex % grid) * cellPxN;
                const homeY = Math.floor(pieceIndex / grid) * cellPxN;
                const snapTol = cellPxN * 0.38;
                if (Math.abs(nx - homeX) <= snapTol && Math.abs(ny - homeY) <= snapTol) {
                    nx = homeX;
                    ny = homeY;
                }

                if (fromTray) {
                    setBoardPieces((prev) => [...prev, { id: newBoardPieceId(), pieceIndex, x: nx, y: ny }]);
                } else if (boardId) {
                    setBoardPieces((prev) => prev.map((b) => (b.id === boardId ? { ...b, x: nx, y: ny } : b)));
                }
                setDragging(null);
                return;
            }

            if (!fromTray && boardId && trayRef.current) {
                const tr = trayRef.current.getBoundingClientRect();
                if (e.clientX >= tr.left && e.clientX < tr.right && e.clientY >= tr.top && e.clientY < tr.bottom) {
                    setBoardPieces((prev) => prev.filter((b) => b.id !== boardId));
                }
            }

            setDragging(null);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [dragging, grid]);

    const handleReset = useCallback(() => setBoardPieces([]), []);
    const placedCount = boardPieces.length;
    const tol = cellPx * 0.22;
    const isSolved =
        total > 0 &&
        boardPieces.length === total &&
        boardPieces.every((b) => {
            const hx = (b.pieceIndex % grid) * cellPx;
            const hy = Math.floor(b.pieceIndex / grid) * cellPx;
            return Math.abs(b.x - hx) < tol && Math.abs(b.y - hy) < tol;
        });
    const piecesNotOnBoard = Array.from({ length: total }, (_, i) => i).filter(
        (p) => !boardPieces.some((b) => b.pieceIndex === p),
    );

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>{grid}×{grid}</Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
                <Box sx={{ width: BOARD_SIZE }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Pieces — free place on board; drop here to remove</Typography>
                    <Box
                        ref={trayRef}
                        sx={{ position: "relative", width: BOARD_SIZE, height: BOARD_SIZE, bgcolor: "grey.900", borderRadius: 1, overflow: "visible" }}
                    >
                        {piecesNotOnBoard.map((pieceIndex) => {
                            const isDragging = dragging?.pieceIndex === pieceIndex && dragging?.fromTray;
                            return (
                                <div
                                    key={pieceIndex}
                                    onMouseDown={(e) => handleMouseDown(e, pieceIndex, { fromTray: true })}
                                    style={{
                                        position: "absolute",
                                        left: (pieceIndex % grid) * cellPx,
                                        top: Math.floor(pieceIndex / grid) * cellPx,
                                        cursor: "grab",
                                        opacity: isDragging ? 0.3 : 1,
                                        zIndex: 1,
                                    }}
                                >
                                    <PieceVisual pieceIndex={pieceIndex} imageUrl={imageUrl} grid={grid} paths={paths} />
                                </div>
                            );
                        })}
                        <JigsawDieLineOverlay pieces={paths} opacity={0.6} svgPathFallback={svg} zIndex={2} />
                    </Box>
                </Box>

                <Box sx={{ width: BOARD_SIZE }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                        <Typography variant="subtitle2">Board ({placedCount}/{total})</Typography>
                        <Button size="small" variant="outlined" onClick={handleReset}>Reset</Button>
                        {placedCount === total && isSolved && (
                            <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700 }}>Solved!</Typography>
                        )}
                    </Box>
                    <Box
                        ref={boardRef}
                        sx={{
                            position: "relative",
                            width: BOARD_SIZE,
                            height: BOARD_SIZE,
                            bgcolor: "grey.800",
                            borderRadius: 1,
                            overflow: "visible",
                            outline: dragging ? "2px dashed #3BA666" : "none",
                            outlineOffset: 0,
                        }}
                    >
                        <JigsawDieLineOverlay pieces={paths} opacity={0.3} svgPathFallback={svg} />
                        {boardPieces.map((b, idx) => {
                            if (dragging?.boardId === b.id) return null;
                            return (
                                <div
                                    key={b.id}
                                    onMouseDown={(e) =>
                                        handleMouseDown(e, b.pieceIndex, {
                                            fromTray: false,
                                            boardId: b.id,
                                            boardX: b.x,
                                            boardY: b.y,
                                        })
                                    }
                                    style={{
                                        position: "absolute",
                                        left: b.x,
                                        top: b.y,
                                        cursor: "grab",
                                        zIndex: 1 + idx,
                                    }}
                                >
                                    <PieceVisual pieceIndex={b.pieceIndex} imageUrl={imageUrl} grid={grid} paths={paths} />
                                </div>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {dragging && (
                <div
                    style={{
                        position: "fixed",
                        left: dragging.x - dragging.grabDx,
                        top: dragging.y - dragging.grabDy,
                        pointerEvents: "none",
                        zIndex: 9999,
                    }}
                >
                    <PieceVisual pieceIndex={dragging.pieceIndex} imageUrl={imageUrl} grid={grid} paths={paths} />
                </div>
            )}
        </Box>
    );
}

export default function TestJigsawMain() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [story, setStory] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = await ApiService.getData(`/v1/story/${STORY_ID}`);
                if (cancelled) return;
                setStory(data);
            } catch (e) {
                if (cancelled) return;
                Logger.error("TestJigsawMain:", e);
                setError(e.message || "Failed to load");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const imageUrl = story?.imageUrl ?? story?.image_url ?? null;

    return (
        <Box sx={{ p: 2, maxWidth: 2200, mx: "auto", userSelect: "none" }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/system/anything")} sx={{ mb: 2 }}>Back</Button>
            <Typography variant="h5" gutterBottom>Test Jigsaw Puzzle</Typography>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : !imageUrl ? (
                <Typography color="text.secondary">No image URL</Typography>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {/* Original image */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Original</Typography>
                        <Box component="img" src={imageUrl} alt="Story" referrerPolicy="no-referrer" sx={{ width: BOARD_SIZE, height: BOARD_SIZE, objectFit: "cover", borderRadius: 1, border: 1, borderColor: "divider", display: "block" }} />
                    </Box>
                    {/* 8×8 */}
                    <JigsawSection imageUrl={imageUrl} grid={8} paths={piecePaths8} svg={jigsawSvg8} />
                    {/* 10×10 */}
                    <JigsawSection imageUrl={imageUrl} grid={10} paths={piecePaths10} svg={jigsawSvg10} />
                    {/* 12×12 */}
                    <JigsawSection imageUrl={imageUrl} grid={12} paths={piecePaths12} svg={jigsawSvg12} />
                </Box>
            )}
        </Box>
    );
}
